<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { apiGet } from "../api";

interface ConversationMessage { role: "bot" | "user" | "event"; text: string }
interface CheckoutDetail {
  checkoutId: string; step: string; product?: string; converted: boolean;
  orderId?: string; createdAt?: string; updatedAt?: string; conversation: ConversationMessage[];
}

const STEP: Record<string, string> = {
  kiezen: "Kiezen", maat: "Maat kiezen", bevestig: "Bevestigen", sokken: "Sokken-aanbod",
  adres: "Adres", bezorging: "Bezorging", betalen: "Betalen", afgerond: "Afgerond",
};
const tijd = (s?: string) => (s ? new Date(s).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.id as string);
const detail = ref<CheckoutDetail | null>(null);
const loading = ref(true);
const error = ref("");

onMounted(async () => {
  try { detail.value = await apiGet<CheckoutDetail>(`/checkout-log/${id.value}`, { auth: true }); }
  catch (e) { error.value = String(e); } finally { loading.value = false; }
});
</script>

<template>
  <div>
    <p v-if="loading" class="muted">Laden…</p>
    <p v-else-if="error" class="err">{{ error }}</p>
    <template v-else-if="detail">
      <header class="dhead">
        <button class="back" @click="router.push('/checkouts')">←</button>
        <h1 class="title">{{ detail.product || 'Checkout' }}</h1>
        <span class="status" :class="{ ok: detail.converted }">{{ detail.converted ? 'Besteld' : 'Afgehaakt' }}</span>
      </header>

      <div class="meta">
        <div><span class="kl">Bereikte stap</span><strong>{{ STEP[detail.step] || detail.step }}</strong></div>
        <div><span class="kl">Gestart</span><strong>{{ tijd(detail.createdAt) }}</strong></div>
        <div><span class="kl">Laatst actief</span><strong>{{ tijd(detail.updatedAt) }}</strong></div>
        <div v-if="detail.converted">
          <span class="kl">Bestelling</span>
          <RouterLink v-if="detail.orderId" :to="`/bestellingen/${detail.orderId}`" class="link">Bekijk order →</RouterLink>
        </div>
      </div>

      <div class="card">
        <h2 class="ct">Gesprek</h2>
        <div v-if="detail.conversation.length" class="chat">
          <div v-for="(m, i) in detail.conversation" :key="i" class="bubble" :class="m.role">{{ m.text }}</div>
        </div>
        <p v-else class="muted">Nog geen berichten.</p>
      </div>
    </template>
  </div>
</template>

<style scoped>
.title { font-family: var(--font-ui); font-weight: 700; font-size: 24px; margin: 0; }
.muted { color: var(--muted); } .err { color: var(--red); }
.dhead { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.back { font-size: 20px; border: 0; background: none; cursor: pointer; }
.status { font-size: 13px; font-weight: 700; color: var(--amber); }
.status.ok { color: var(--green); }
.meta { display: grid; grid-template-columns: repeat(4, auto); gap: 28px; margin-bottom: 18px; }
.meta > div { display: flex; flex-direction: column; gap: 4px; }
.kl { font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); }
.link { color: var(--coral); text-decoration: none; font-weight: 600; }
.card { background: var(--surface); border: 1px solid var(--hairline); border-radius: 14px; padding: 18px; max-width: 720px; }
.ct { font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); margin: 0 0 12px; }
.chat { display: flex; flex-direction: column; gap: 8px; }
.bubble { max-width: 75%; padding: 9px 13px; border-radius: 14px; font-size: 14px; line-height: 1.35; }
.bubble.bot { align-self: flex-start; background: var(--warm); border-bottom-left-radius: 4px; }
.bubble.user { align-self: flex-end; background: var(--coral); color: #fff; border-bottom-right-radius: 4px; }
.bubble.event { align-self: center; max-width: 90%; background: transparent; color: var(--muted); font-size: 12px; font-style: italic; padding: 2px 8px; }
</style>
